import React, { useState, useEffect, useCallback } from 'react';
import { authFetchInit, getAuthHeaders, hasAuthSession } from '../utils/authSession';
import { useNavigate } from 'react-router-dom';
import {
  Box, Grid, Card, CardContent, Typography, Chip, Avatar, IconButton,
  useTheme, Button, LinearProgress, List, ListItem, ListItemIcon,
  ListItemText, Divider,
} from '@mui/material';
import {
  People, Apps, Security, TrendingUp, TrendingDown, Business, Shield,
  CheckCircle, VpnKey, AccountCircle, Refresh, Dashboard as DashboardIcon,
  Assignment, Policy as PolicyIcon, RateReview as RateReviewIcon,
  PendingActions, TaskAlt, RadioButtonUnchecked, Login as LoginIcon,
  FactCheck as QuizIcon, AccountTree as IGAIcon,
  SmartToy as AIIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { filterActionsByRouteAccess } from '../utils/rolePermissions';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { DashboardStats } from '../types';
import { dashboardAPI, applicationsAPI, ssoAPI } from '../services/api';
import { API_ROOT } from '../config/apiConfig';
import { questionCategories } from '../utils/riskQuestionnaire';
import CenteredLoader from '../components/CenteredLoader';
import AdminInbox from '../components/AdminInbox';

// ─── Shared style tokens ──────────────────────────────────────────────────────
const LABEL_SX = { fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.5, color: 'text.secondary' };
const BODY_SX  = { fontSize: '0.82rem' };
const SEC_SX   = { fontSize: '0.78rem', color: 'text.secondary' };
const STAT_SX  = { fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.1 };
const CARD_SX  = { border: '1px solid', borderColor: 'divider', boxShadow: 'none', height: '100%' };
const CC_SX    = { p: 2, '&:last-child': { pb: 2 } };

type DashboardAction = { label: string; icon: React.ReactNode; path: string; variant?: 'contained' | 'outlined' };

const actionsForRole = (role: string | undefined, actions: DashboardAction[]) =>
  filterActionsByRouteAccess(role, actions);

// ─── Stat Card ────────────────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  sub?: string;
  onClick?: () => void;
}
const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color, sub, onClick }) => (
  <Card sx={{ ...CARD_SX, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { borderColor: color, boxShadow: `0 0 0 1px ${color}` } : {} }} onClick={onClick}>
    <CardContent sx={CC_SX}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography sx={LABEL_SX}>{label}</Typography>
          <Typography sx={{ ...STAT_SX, color, mt: 0.5 }}>{value}</Typography>
          {sub && <Typography sx={{ ...SEC_SX, mt: 0.5 }}>{sub}</Typography>}
        </Box>
        <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

// ─── Progress Row Card ────────────────────────────────────────────────────────
interface ProgressCardProps { label: string; value: number; total: number; color: string; icon: React.ReactNode; onClick?: () => void }
const ProgressCard: React.FC<ProgressCardProps> = ({ label, value, total, color, icon, onClick }) => {
  const pct = total > 0 ? Math.min((value / total) * 100, 100) : 0;
  return (
    <Card sx={{ ...CARD_SX, cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { borderColor: color } : {} }} onClick={onClick}>
      <CardContent sx={CC_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
            {icon}
          </Box>
          <Box>
            <Typography sx={{ ...BODY_SX, fontWeight: 600 }}>{label}</Typography>
            <Typography sx={SEC_SX}>{value} of {total} applications</Typography>
          </Box>
        </Box>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3, bgcolor: `${color}20`, '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }} />
        <Typography sx={{ ...SEC_SX, mt: 0.75, fontWeight: 600, color }}>{pct.toFixed(1)}%</Typography>
      </CardContent>
    </Card>
  );
};

// ─── Section header ───────────────────────────────────────────────────────────
const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; action?: React.ReactNode }> = ({ icon, title, action }) => (
  <Box sx={{ px: 2, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{title}</Typography>
    </Box>
    {action}
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const { colors } = useCustomTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [userStats, setUserStats] = useState({ totalOwnedApps: 0, ssoOnboarded: 0, igaOnboarded: 0, pamOnboarded: 0, pendingReview: 0, inProgress: 0, approved: 0, completed: 0, rejected: 0 });
  const [grcStats, setGrcStats] = useState({ total: 0, inProgress: 0, submitted: 0, approved: 0, notStarted: 0 });
  const [grcQuestions, setGrcQuestions] = useState<{ category: string; count: number }[]>([]);
  const [grcPolicies, setGrcPolicies] = useState<{ risk_level: string; policy_name: string }[]>([]);

  // ── Loaders ─────────────────────────────────────────────────────────────────
  const loadGRCStats = useCallback(async () => {
    const h = {  };
    try {
      let templateId: string | null = null;
      const tplRes = await fetch(`${API_ROOT}/risk-assessment/template`, authFetchInit({ headers: h }));
      if (tplRes.ok) {
        const tplData = await tplRes.json();
        templateId = tplData?.template?.id ?? null;
      }

      const [overviewRes, policiesRes] = await Promise.all([
        fetch(`${API_ROOT}/risk-assessment/admin/all-assessments`, authFetchInit({ headers: h })),
        fetch(`${API_ROOT}/okta/policy-mappings`, authFetchInit({ headers: h })),
      ]);
      if (overviewRes.ok) {
        const data = await overviewRes.json();
        const apps: any[] = data.applications || [];
        setGrcStats({
          total: apps.length,
          inProgress: apps.filter(a => a.assessment?.status === 'Draft').length,
          submitted: apps.filter(a => a.assessment?.status === 'In Review').length,
          approved: apps.filter(a => a.assessment?.status === 'Approved').length,
          notStarted: apps.filter(a => !a.assessment).length,
        });
      }
      if (templateId) {
        const questionsRes = await fetch(`${API_ROOT}/risk-assessment/questions/${templateId}`, authFetchInit({ headers: h }));
        if (questionsRes.ok) {
          const data = await questionsRes.json();
          const byCategory: Record<string, number> = {};
          (data.questions || []).forEach((q: any) => {
            questionCategories(q).forEach((cat: string) => {
              byCategory[cat] = (byCategory[cat] || 0) + 1;
            });
          });
          setGrcQuestions(Object.entries(byCategory).map(([category, count]) => ({ category, count })));
        }
      }
      if (policiesRes.ok) {
        const data = await policiesRes.json();
        setGrcPolicies(data.mappings || []);
      }
    } catch (e) {
      console.error('GRC stats error', e);
    }
  }, []);

  const isApplicationOwner = (app: any) =>
    app.primary_owners?.some((o: any) => o.user_id === user?.id) ||
    app.secondary_owners?.some((o: any) => o.user_id === user?.id);

  const isApplicationDelegate = (app: any) => {
    const name = `${user?.first_name || ''} ${user?.last_name || ''}`.trim().toLowerCase();
    const email = (user?.email || '').toLowerCase();
    const instances = app.instances || app.application_instances || [];
    return instances.some((inst: any) => {
      const delegated = (inst.delegatedTo || inst.delegated_to || '').toLowerCase();
      return delegated && (delegated === name || delegated === email || delegated.includes(email));
    });
  };

  const loadUserSpecificData = useCallback(async () => {
    try {
      const [userApps, ssoRes] = await Promise.all([applicationsAPI.getApplications(), ssoAPI.getConfigurationsByUser()]);
      const ssoPayload = ssoRes.data;
      const cfgs: any[] = Array.isArray(ssoPayload?.data)
        ? ssoPayload.data
        : Array.isArray(ssoPayload)
          ? ssoPayload
          : [];
      const scopedApps = user?.role === 'delegate'
        ? userApps.filter(isApplicationDelegate)
        : userApps.filter(isApplicationOwner);
      const scopedAppIds = new Set(scopedApps.map((a: any) => a.id));
      const scopedCfgs = cfgs.filter((c: any) => scopedAppIds.has(c.application_id));
      setUserStats({
        totalOwnedApps: scopedApps.length,
        ssoOnboarded: scopedCfgs.filter(c => c.status === 'Completed' || c.status === 'Approved').length,
        igaOnboarded: 0, pamOnboarded: 0,
        pendingReview: scopedCfgs.filter(c => c.status === 'Pending Review').length,
        inProgress: scopedCfgs.filter(c => c.status === 'In Progress').length,
        approved: scopedCfgs.filter(c => c.status === 'Approved').length,
        completed: scopedCfgs.filter(c => c.status === 'Completed').length,
        rejected: scopedCfgs.filter(c => c.status === 'Rejected').length,
      });
    } catch (e) { console.error('User stats error', e); }
  }, [user?.id, user?.role, user?.email, user?.first_name, user?.last_name]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const s = await dashboardAPI.getStats();
        setStats(s);
        if (['user', 'app_admin', 'delegate'].includes(user?.role || '')) await loadUserSpecificData();
        else if (user?.role === 'grc_admin') await loadGRCStats();
      } catch { }
      setLoading(false);
    })();
  }, [user?.role, loadUserSpecificData, loadGRCStats]);

  if (loading) return <CenteredLoader />;

  const ps = stats?.platformStats;
  const actColor = (t: string) => t === 'application' ? colors.primary : t === 'user' ? theme.palette.secondary.main : theme.palette.success.main;
  const actIcon  = (t: string) => t === 'application' ? <Apps sx={{ fontSize: 16 }} /> : t === 'user' ? <People sx={{ fontSize: 16 }} /> : <Security sx={{ fontSize: 16 }} />;

  // ── Shared bottom: Recent Activities + Quick Actions ─────────────────────────
  const BottomSection = ({ actions }: { actions: { label: string; icon: React.ReactNode; path: string; variant?: 'contained' | 'outlined' }[] }) => (
    <Grid container spacing={2}>
      {/* Recent Activities */}
      <Grid item xs={12} md={8}>
        <Card sx={CARD_SX}>
          <SectionHeader icon={<TrendingUp sx={{ fontSize: 16 }} />} title="Recent Activities"
            action={<IconButton size="small" onClick={() => window.location.reload()}><Refresh sx={{ fontSize: 16 }} /></IconButton>} />
          {stats?.recentActivities && stats.recentActivities.length > 0 ? (
            <List dense disablePadding>
              {stats.recentActivities.slice(0, 6).map((a, i) => (
                <React.Fragment key={a.id}>
                  <ListItem sx={{ px: 2, py: 0.75 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: `${actColor(a.type)}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: actColor(a.type) }}>
                        {actIcon(a.type)}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ ...BODY_SX, fontWeight: 600 }}>{a.action}</Typography>}
                      secondary={<Typography sx={SEC_SX}>{a.target}</Typography>}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>
                        {(() => {
                          const raw = a.timestamp || a.time;
                          const parsed = new Date(raw);
                          return Number.isNaN(parsed.getTime()) ? a.time : parsed.toLocaleString();
                        })()}
                      </Typography>
                      <Chip label={a.type} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    </Box>
                  </ListItem>
                  {i < Math.min(stats.recentActivities.length, 6) - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={SEC_SX}>No recent activities</Typography>
            </Box>
          )}
        </Card>
      </Grid>

      {/* Quick Actions */}
      <Grid item xs={12} md={4}>
        <Card sx={CARD_SX}>
          <SectionHeader icon={<Assignment sx={{ fontSize: 16 }} />} title="Quick Actions" />
          <CardContent sx={{ ...CC_SX, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {actions.map((a, i) => (
              <Button key={i} variant={a.variant || 'outlined'} size="small" startIcon={a.icon}
                fullWidth sx={{ justifyContent: 'flex-start', fontSize: '0.78rem', ...(a.variant === 'contained' && { background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.dark} 100%)` }) }}
                onClick={() => navigate(a.path)}>
                {a.label}
              </Button>
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  // ── Welcome Header ───────────────────────────────────────────────────────────
  const WelcomeBar = ({ subtitle }: { subtitle: string }) => (
    <Box sx={{ mb: 2.5, p: 2, background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.dark} 100%)`, borderRadius: 2, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box>
        <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>Welcome back, {user?.first_name || user?.username}!</Typography>
        <Typography sx={{ fontSize: '0.78rem', opacity: 0.85, mt: 0.25 }}>{subtitle}</Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Chip icon={<DashboardIcon sx={{ fontSize: '0.85rem !important' }} />} label={new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.72rem', height: 24 }} />
        <Chip icon={<AccountCircle sx={{ fontSize: '0.85rem !important' }} />} label={(user?.role || 'user').replace(/_/g, ' ').toUpperCase()} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.72rem', height: 24 }} />
      </Box>
    </Box>
  );

  const ownedAppCount = stats?.userOwnership?.totalOwned || userStats.totalOwnedApps || stats?.totalApplications || 0;

  // ────────────────────────────────────────────────────────────────────────────
  // ROLE: user / app_admin / delegate (application-scoped)
  // ────────────────────────────────────────────────────────────────────────────
  if (user?.role === 'user' || user?.role === 'app_admin' || user?.role === 'delegate') return (
    <Box>
      <WelcomeBar subtitle={
        user?.role === 'delegate'
          ? 'Delegated applications and onboarding progress'
          : user?.role === 'app_admin'
            ? 'Applications you own and entitlements overview'
            : 'Your application portfolio and onboarding progress'
      } />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <StatCard
            label={user?.role === 'delegate' ? 'Delegated Apps' : 'My Applications'}
            value={ownedAppCount}
            icon={<Apps sx={{ fontSize: 20 }} />}
            color="#6B7280"
            sub={user?.role === 'delegate' ? 'Applications assigned to you' : 'Applications you own or manage'}
            onClick={() => navigate('/applications')}
          />
        </Grid>
        {user?.role === 'app_admin' && (
          <Grid item xs={6} sm={3}>
            <StatCard label="Entitlements" value={stats?.totalEntitlements || 0} icon={<Security sx={{ fontSize: 20 }} />} color="#7C3AED" sub="On your applications" />
          </Grid>
        )}
        <Grid item xs={6} sm={3}><StatCard label="SSO Onboarded" value={userStats.ssoOnboarded} icon={<VpnKey sx={{ fontSize: 20 }} />} color="#059669" sub="Single Sign-On ready" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="SSO In Progress" value={userStats.inProgress + userStats.pendingReview} icon={<PendingActions sx={{ fontSize: 20 }} />} color="#f59e0b" sub="Pending or in progress" /></Grid>
        {user?.role !== 'app_admin' && (
          <Grid item xs={6} sm={3}><StatCard label="Primary Owner" value={stats?.userOwnership?.primaryOwner || 0} icon={<Business sx={{ fontSize: 20 }} />} color="#0F766E" sub="Primary ownership" /></Grid>
        )}
      </Grid>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}><ProgressCard label="SSO Onboarding" value={userStats.ssoOnboarded} total={ownedAppCount} color="#059669" icon={<VpnKey sx={{ fontSize: 16 }} />} onClick={() => navigate('/applications')} /></Grid>
        <Grid item xs={12} md={4}><ProgressCard label="IGA Onboarding" value={ps?.IGA?.completed || userStats.igaOnboarded} total={ownedAppCount || ps?.IGA?.total || 0} color="#0F766E" icon={<IGAIcon sx={{ fontSize: 16 }} />} onClick={() => navigate('/applications/iga-onboarding')} /></Grid>
        <Grid item xs={12} md={4}><ProgressCard label="PAM Onboarding" value={ps?.PAM?.completed || userStats.pamOnboarded} total={ownedAppCount || ps?.PAM?.total || 0} color="#7C3AED" icon={<Shield sx={{ fontSize: 16 }} />} /></Grid>
      </Grid>
      <BottomSection actions={actionsForRole(user?.role, [
        { label: 'View Applications', icon: <Apps sx={{ fontSize: 16 }} />, path: '/applications', variant: 'contained' },
        { label: 'IGA Onboarding', icon: <IGAIcon sx={{ fontSize: 16 }} />, path: '/applications/iga-onboarding' },
        ...(user?.role === 'app_admin' ? [{ label: 'Reports', icon: <TrendingUp sx={{ fontSize: 16 }} />, path: '/reports' }] : []),
      ])} />
    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // ROLE: iga_admin
  // ────────────────────────────────────────────────────────────────────────────
  if (user?.role === 'iga_admin') return (
    <Box>
      <WelcomeBar subtitle="Identity governance and application onboarding overview" />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><StatCard label="Applications" value={stats?.totalApplications || 0} icon={<Apps sx={{ fontSize: 20 }} />} color={colors.primary} sub="Enterprise applications" onClick={() => navigate('/applications')} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Entitlements" value={stats?.totalEntitlements || 0} icon={<Security sx={{ fontSize: 20 }} />} color="#7C3AED" sub="Total entitlements" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="IGA Completed" value={ps?.IGA?.completed || 0} icon={<CheckCircle sx={{ fontSize: 20 }} />} color="#0F766E" sub="Onboarded via IGA" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="IGA In Progress" value={ps?.IGA?.inProgress || 0} icon={<PendingActions sx={{ fontSize: 20 }} />} color="#f59e0b" sub="In progress" /></Grid>
      </Grid>
      <BottomSection actions={[
        { label: 'IGA Onboarding', icon: <IGAIcon sx={{ fontSize: 16 }} />, path: '/applications/iga-onboarding', variant: 'contained' },
        { label: 'Applications', icon: <Apps sx={{ fontSize: 16 }} />, path: '/applications' },
        { label: 'Reports', icon: <TrendingUp sx={{ fontSize: 16 }} />, path: '/reports' },
        { label: 'Audit Trail', icon: <Assignment sx={{ fontSize: 16 }} />, path: '/audit-trail' },
      ]} />
    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // ROLE: pam_admin
  // ────────────────────────────────────────────────────────────────────────────
  if (user?.role === 'pam_admin') return (
    <Box>
      <WelcomeBar subtitle="Privileged access management overview" />
      <AdminInbox />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><StatCard label="Applications" value={stats?.totalApplications || 0} icon={<Apps sx={{ fontSize: 20 }} />} color={colors.primary} sub="Enterprise applications" onClick={() => navigate('/applications')} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="PAM Completed" value={ps?.PAM?.completed || 0} icon={<CheckCircle sx={{ fontSize: 20 }} />} color="#7C3AED" sub="PAM onboarded" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="PAM In Progress" value={ps?.PAM?.inProgress || 0} icon={<PendingActions sx={{ fontSize: 20 }} />} color="#f59e0b" sub="In progress" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Entitlements" value={stats?.totalEntitlements || 0} icon={<Security sx={{ fontSize: 20 }} />} color="#6366f1" sub="Total entitlements" /></Grid>
      </Grid>
      <BottomSection actions={[
        { label: 'Applications', icon: <Apps sx={{ fontSize: 16 }} />, path: '/applications', variant: 'contained' },
        { label: 'Reports', icon: <TrendingUp sx={{ fontSize: 16 }} />, path: '/reports' },
        { label: 'Audit Trail', icon: <Assignment sx={{ fontSize: 16 }} />, path: '/audit-trail' },
      ]} />
    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // ROLE: auditor
  // ────────────────────────────────────────────────────────────────────────────
  if (user?.role === 'auditor') return (
    <Box>
      <WelcomeBar subtitle="Read-only enterprise compliance overview" />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><StatCard label="Applications" value={stats?.totalApplications || 0} icon={<Apps sx={{ fontSize: 20 }} />} color={colors.primary} sub="Enterprise applications" onClick={() => navigate('/applications')} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Entitlements" value={stats?.totalEntitlements || 0} icon={<Security sx={{ fontSize: 20 }} />} color="#7C3AED" sub="Total entitlements" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="SSO Completed" value={ps?.SSO?.completed || 0} icon={<CheckCircle sx={{ fontSize: 20 }} />} color="#059669" sub="SSO onboarded" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="SSO In Progress" value={ps?.SSO?.inProgress || 0} icon={<PendingActions sx={{ fontSize: 20 }} />} color="#f59e0b" sub="Pending review" /></Grid>
      </Grid>
      <BottomSection actions={[
        { label: 'Audit Trail', icon: <Assignment sx={{ fontSize: 16 }} />, path: '/audit-trail', variant: 'contained' },
        { label: 'Applications', icon: <Apps sx={{ fontSize: 16 }} />, path: '/applications' },
        { label: 'Reports', icon: <TrendingUp sx={{ fontSize: 16 }} />, path: '/reports' },
      ]} />
    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // ROLE: sso_admin
  // ────────────────────────────────────────────────────────────────────────────
  if (user?.role === 'sso_admin') return (
    <Box>
      <WelcomeBar subtitle="SSO configurations and onboarding overview" />
      <AdminInbox />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><StatCard label="Total Applications" value={stats?.totalApplications || 0} icon={<Apps sx={{ fontSize: 20 }} />} color={colors.primary} sub="Across enterprise" onClick={() => navigate('/applications')} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="SSO Configurations" value={ps?.SSO?.total || 0} icon={<LoginIcon sx={{ fontSize: 20 }} />} color="#059669" sub="All SSO requests" onClick={() => navigate('/sso-review')} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Completed" value={ps?.SSO?.completed || 0} icon={<CheckCircle sx={{ fontSize: 20 }} />} color="#0F766E" sub="Fully onboarded" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="In Progress" value={ps?.SSO?.inProgress || 0} icon={<PendingActions sx={{ fontSize: 20 }} />} color="#f59e0b" sub="Pending review or config" /></Grid>
      </Grid>

      {/* SSO Progress bar */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card sx={CARD_SX}>
            <SectionHeader icon={<LoginIcon sx={{ fontSize: 16 }} />} title="SSO Onboarding Progress" />
            <CardContent sx={CC_SX}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography sx={BODY_SX}>Completed vs Total</Typography>
                <Typography sx={{ ...BODY_SX, fontWeight: 700, color: colors.primary }}>
                  {ps?.SSO?.total ? Math.round((ps.SSO.completed / ps.SSO.total) * 100) : 0}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={ps?.SSO?.total ? Math.min((ps.SSO.completed / ps.SSO.total) * 100, 100) : 0}
                sx={{ height: 8, borderRadius: 4, bgcolor: `${colors.primary}20`, '& .MuiLinearProgress-bar': { bgcolor: colors.primary, borderRadius: 4 } }} />
              <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
                {[
                  { label: 'Completed', val: ps?.SSO?.completed || 0, color: '#059669' },
                  { label: 'In Progress', val: ps?.SSO?.inProgress || 0, color: '#f59e0b' },
                  { label: 'Remaining', val: Math.max((ps?.SSO?.total || 0) - (ps?.SSO?.completed || 0) - (ps?.SSO?.inProgress || 0), 0), color: '#9ca3af' },
                ].map(s => (
                  <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{s.label}: <b style={{ color: s.color }}>{s.val}</b></Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={CARD_SX}>
            <SectionHeader icon={<RateReviewIcon sx={{ fontSize: 16 }} />} title="Pending Review Queue" />
            <CardContent sx={CC_SX}>
              {[
                { label: 'Pending Review', val: ps?.SSO?.inProgress || 0, color: '#f59e0b' },
                { label: 'Need More Info', val: 0, color: '#ef4444' },
                { label: 'Approved — awaiting provisioning', val: 0, color: '#3b82f6' },
              ].map(r => (
                <Box key={r.label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: r.color }} />
                    <Typography sx={BODY_SX}>{r.label}</Typography>
                  </Box>
                  <Chip label={r.val} size="small" sx={{ height: 20, fontSize: '0.72rem', bgcolor: `${r.color}15`, color: r.color, fontWeight: 700 }} />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <BottomSection actions={[
        { label: 'SSO Review Queue', icon: <RateReviewIcon sx={{ fontSize: 16 }} />, path: '/sso-review', variant: 'contained' },
        { label: 'View Applications', icon: <Apps sx={{ fontSize: 16 }} />, path: '/applications' },
        { label: 'Audit Trail', icon: <Assignment sx={{ fontSize: 16 }} />, path: '/audit-trail' },
        { label: 'Reports', icon: <TrendingUp sx={{ fontSize: 16 }} />, path: '/reports' },
      ]} />
    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // ROLE: grc_admin
  // ────────────────────────────────────────────────────────────────────────────
  if (user?.role === 'grc_admin') return (
    <Box>
      <WelcomeBar subtitle="Risk assessment and governance overview" />
      <AdminInbox />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'Total Applications', value: grcStats.total, icon: <Apps sx={{ fontSize: 20 }} />, color: '#6366f1', path: '/risk-assessment-review' },
          { label: 'Not Started', value: grcStats.notStarted, icon: <RadioButtonUnchecked sx={{ fontSize: 20 }} />, color: '#9ca3af' },
          { label: 'In Review', value: grcStats.inProgress + grcStats.submitted, icon: <PendingActions sx={{ fontSize: 20 }} />, color: '#f59e0b', path: '/risk-assessment-review' },
          { label: 'Approved', value: grcStats.approved, icon: <TaskAlt sx={{ fontSize: 20 }} />, color: '#10b981', path: '/risk-assessment-review' },
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <StatCard label={s.label} value={s.value} icon={s.icon} color={s.color} onClick={s.path ? () => navigate(s.path!) : undefined} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Risk Questions */}
        <Grid item xs={12} md={6}>
          <Card sx={CARD_SX}>
            <SectionHeader icon={<QuizIcon sx={{ fontSize: 16 }} />} title="Risk Questions"
              action={<Chip label={`${grcQuestions.reduce((s, q) => s + q.count, 0)} total`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />} />
            <CardContent sx={CC_SX}>
              {grcQuestions.length === 0 ? (
                <Typography sx={{ ...SEC_SX, fontStyle: 'italic', py: 1 }}>No questions configured yet</Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
                  {grcQuestions.map(q => (
                    <Chip key={q.category} label={`${q.category}: ${q.count}`} size="small" variant="outlined" color="primary" sx={{ height: 22, fontSize: '0.65rem' }} />
                  ))}
                </Box>
              )}
              <Button size="small" variant="outlined" startIcon={<QuizIcon sx={{ fontSize: 14 }} />} onClick={() => navigate('/risk-questions')} sx={{ fontSize: '0.75rem' }}>
                Manage Questions
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Risk Policies */}
        <Grid item xs={12} md={6}>
          <Card sx={CARD_SX}>
            <SectionHeader icon={<PolicyIcon sx={{ fontSize: 16 }} />} title="Risk → Policy Mapping"
              action={<Chip label={`${grcPolicies.length} / 4 mapped`} size="small" sx={{ height: 20, fontSize: '0.65rem' }} />} />
            <CardContent sx={CC_SX}>
              {(['Low', 'Medium', 'High', 'Critical'] as const).map(level => {
                const mapped = grcPolicies.find(p => p.risk_level === level);
                const c: Record<string, string> = { Low: '#10b981', Medium: '#f59e0b', High: '#f97316', Critical: '#ef4444' };
                return (
                  <Box key={level} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c[level], flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, minWidth: 58, color: c[level] }}>{level}</Typography>
                    <Typography sx={{ ...SEC_SX, fontStyle: mapped ? 'normal' : 'italic', color: mapped ? 'text.primary' : 'text.disabled' }}>
                      {mapped ? mapped.policy_name : '— Not mapped'}
                    </Typography>
                  </Box>
                );
              })}
              <Box sx={{ mt: 1.5 }}>
                <Button size="small" variant="outlined" color="secondary" startIcon={<PolicyIcon sx={{ fontSize: 14 }} />} onClick={() => navigate('/risk-policies')} sx={{ fontSize: '0.75rem' }}>
                  Manage Policies
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <BottomSection actions={[
        { label: 'Risk Assessment Review', icon: <RateReviewIcon sx={{ fontSize: 16 }} />, path: '/risk-assessment-review', variant: 'contained' },
        { label: 'Risk Questions', icon: <QuizIcon sx={{ fontSize: 16 }} />, path: '/risk-questions' },
        { label: 'Risk Policies', icon: <PolicyIcon sx={{ fontSize: 16 }} />, path: '/risk-policies' },
        { label: 'Applications', icon: <Apps sx={{ fontSize: 16 }} />, path: '/applications' },
        { label: 'AI Governance', icon: <AIIcon sx={{ fontSize: 16 }} />, path: '/ai-governance' },
      ]} />
    </Box>
  );

  // ────────────────────────────────────────────────────────────────────────────
  // ROLE: super_admin
  // ────────────────────────────────────────────────────────────────────────────
  if (user?.role !== 'super_admin') {
    return (
      <Box>
        <WelcomeBar subtitle="Dashboard overview" />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} sm={3}><StatCard label="Applications" value={stats?.totalApplications || 0} icon={<Apps sx={{ fontSize: 20 }} />} color={colors.primary} onClick={() => navigate('/applications')} /></Grid>
          <Grid item xs={6} sm={3}><StatCard label="Entitlements" value={stats?.totalEntitlements || 0} icon={<Security sx={{ fontSize: 20 }} />} color="#7C3AED" /></Grid>
        </Grid>
        <BottomSection actions={[{ label: 'Applications', icon: <Apps sx={{ fontSize: 16 }} />, path: '/applications', variant: 'contained' }]} />
      </Box>
    );
  }

  return (
    <Box>
      <WelcomeBar subtitle="Enterprise IAM system overview" />
      <AdminInbox />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}><StatCard label="Total Users" value={stats?.totalUsers || 0} icon={<People sx={{ fontSize: 20 }} />} color="#6B7280" sub="Registered users" onClick={() => navigate('/users')} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Active Users" value={stats?.activeUsers || 0} icon={<TrendingUp sx={{ fontSize: 20 }} />} color="#059669" sub="Currently active" /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Applications" value={stats?.totalApplications || 0} icon={<Apps sx={{ fontSize: 20 }} />} color={colors.primary} sub="Managed apps" onClick={() => navigate('/applications')} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Entitlements" value={stats?.totalEntitlements || 0} icon={<Security sx={{ fontSize: 20 }} />} color="#7C3AED" sub="Total entitlements" /></Grid>
      </Grid>

      {/* Platform breakdown */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {[
          { label: 'SSO Onboarding', completed: ps?.SSO?.completed || 0, total: ps?.SSO?.total || 0, inProgress: ps?.SSO?.inProgress || 0, color: '#059669', icon: <VpnKey sx={{ fontSize: 16 }} />, path: '/sso-review?pipeline=pending' },
          { label: 'IGA Onboarding', completed: ps?.IGA?.completed || 0, total: ps?.IGA?.total || 0, inProgress: ps?.IGA?.inProgress || 0, color: '#0F766E', icon: <IGAIcon sx={{ fontSize: 16 }} />, path: '/applications/iga-onboarding' },
          { label: 'PAM Onboarding', completed: ps?.PAM?.completed || 0, total: ps?.PAM?.total || 0, inProgress: ps?.PAM?.inProgress || 0, color: '#7C3AED', icon: <Shield sx={{ fontSize: 16 }} />, path: '/pam-dashboard' },
        ].map(p => (
          <Grid item xs={12} md={4} key={p.label}>
            <Card sx={{ ...CARD_SX, cursor: 'pointer' }} onClick={() => navigate(p.path)}>
              <CardContent sx={CC_SX}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: 1, bgcolor: `${p.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: p.color }}>{p.icon}</Box>
                  <Box>
                    <Typography sx={{ ...BODY_SX, fontWeight: 600 }}>{p.label}</Typography>
                    <Typography sx={SEC_SX}>{p.completed} of {p.total} completed</Typography>
                  </Box>
                </Box>
                <LinearProgress variant="determinate" value={p.total > 0 ? Math.min((p.completed / p.total) * 100, 100) : 0}
                  sx={{ height: 6, borderRadius: 3, bgcolor: `${p.color}20`, '& .MuiLinearProgress-bar': { bgcolor: p.color, borderRadius: 3 } }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                  <Typography sx={{ ...SEC_SX, color: p.color, fontWeight: 600 }}>
                    {p.total > 0 ? ((p.completed / p.total) * 100).toFixed(1) : 0}%
                  </Typography>
                  <Typography sx={SEC_SX}>
                    {p.label === 'SSO Onboarding' ? `${p.inProgress} pending` : `${p.inProgress} in progress`}
                    {Math.max(0, p.total - p.completed - p.inProgress) > 0
                      ? ` · ${Math.max(0, p.total - p.completed - p.inProgress)} not started or other`
                      : ''}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <BottomSection actions={[
        { label: 'View All Applications', icon: <Apps sx={{ fontSize: 16 }} />, path: '/applications', variant: 'contained' },
        { label: 'IGA Onboarding', icon: <IGAIcon sx={{ fontSize: 16 }} />, path: '/applications/iga-onboarding' },
        { label: 'SSO Review', icon: <RateReviewIcon sx={{ fontSize: 16 }} />, path: '/sso-review' },
        { label: 'Risk Policies', icon: <PolicyIcon sx={{ fontSize: 16 }} />, path: '/risk-policies' },
        { label: 'Users', icon: <People sx={{ fontSize: 16 }} />, path: '/users' },
        { label: 'Audit Trail', icon: <Assignment sx={{ fontSize: 16 }} />, path: '/audit-trail' },
        { label: 'Settings', icon: <Security sx={{ fontSize: 16 }} />, path: '/settings' },
      ]} />
    </Box>
  );
};

export default Dashboard;
